/**
 * The Showings answer ("Yes, most days" / "No, only at set times") is saved on the Listing
 * (`listing.showingsAnswer`, BR-107, BR-112); changing it never deletes saved times. The same saved
 * data decides whether the Showings step is complete and what the rail says about it.
 */

import type { AvailabilityException, AvailabilityRule } from '../../../../lib/leasing.api';
import { exceptionDateKey } from './time-utils';
import { weeklyHours } from './weekly-hours';

export type ShowingsMode = 'general' | 'limited';

type RuleLike = Pick<AvailabilityRule, 'weekdays' | 'startTime' | 'endTime'>;
type ExceptionLike = Pick<AvailabilityException, 'kind' | 'date'>;

function privateShowingDays(exceptions: readonly ExceptionLike[], today?: string): number {
  return exceptions.filter((ex) => ex.kind === 'window' && (!today || exceptionDateKey(ex.date) >= today)).length;
}

/**
 * The answer saved times imply, for a LIVE listing that has times but no saved answer (a row from before
 * the answer was stored that the data backfill has not reached): weekly hours mean "Yes, most days"; else
 * a private-showing date or an open house means "No, only at set times"; no times means no answer. The
 * same mapping the backfill migration applies. It only keeps those times visible: nothing is saved.
 */
export function impliedShowingsAnswer(
  rules: readonly RuleLike[],
  exceptions: readonly ExceptionLike[],
  openHouses: readonly unknown[]
): ShowingsMode | null {
  if (rules.length > 0) return 'general';
  if (privateShowingDays(exceptions) > 0 || openHouses.length > 0) return 'limited';
  return null;
}

/**
 * BR-107: complete once an answer is saved AND a weekly rule, a private-showing date or an open house
 * exists. Either alone is not enough.
 */
export function isShowingsComplete(
  answer: ShowingsMode | null | undefined,
  rules: readonly RuleLike[],
  exceptions: readonly ExceptionLike[],
  openHouses: readonly unknown[],
  /** Optional `YYYY-MM-DD` (listing timezone): when given, private-showing dates before it do not count. */
  today?: string
): boolean {
  if (!answer) return false;
  return rules.length > 0 || privateShowingDays(exceptions, today) > 0 || openHouses.length > 0;
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** The one-line rail summary, read from saved data. */
export function describeShowings(
  rules: readonly RuleLike[],
  exceptions: readonly ExceptionLike[],
  openHouses: readonly unknown[],
  durationMinutes?: number | null,
  today?: string
): string {
  const parts: string[] = [];
  if (rules.length > 0) {
    parts.push(`About ${weeklyHours(rules)} ${weeklyHours(rules) === 1 ? 'hour' : 'hours'} a week`);
  } else {
    const days = privateShowingDays(exceptions, today);
    if (days > 0) parts.push(plural(days, 'day', 'days'));
  }
  if (openHouses.length > 0) parts.push(plural(openHouses.length, 'open house', 'open houses'));
  if (parts.length === 0) return 'Not set yet. Prospects cannot book a showing.';
  if (durationMinutes) parts.push(`${durationMinutes} min showings`);
  return parts.join(' · ');
}
